import * as React from 'react';

/**
 * Keep the live PubNub listener subscribed for a short grace window after an
 * activity stops being in-progress.
 *
 * When a teacher finishes a student-paced ("open") activity, the student app
 * flushes the answer on the question the student was on. That save is accepted
 * server-side within ACTIVITY_END_GRACE_SECONDS and the backend publishes a
 * `studentResponse` message. Without a grace window the teacher tears the live
 * listener down the instant the activity flips to Finished, so that final
 * response is never received live (it only shows on a manual refresh).
 *
 * Holding the listener open briefly lets the late `studentResponse` land in the
 * results cache, which the (now Finished) report view reads from.
 *
 * Kept in step with the student-service ACTIVITY_END_GRACE_SECONDS (5s): the
 * teacher must still be listening for as long as the server will accept a
 * late teacher-finish flush. The real flush round-trips in ~1-2s, so 5s is
 * comfortable headroom.
 */
export const LIVE_LISTENER_GRACE_MS = 5_000;

export function useLiveListenerGrace(isInProgress: boolean): boolean {
  const [active, setActive] = React.useState(isInProgress);

  React.useEffect(() => {
    if (isInProgress) {
      setActive(true);
      return;
    }
    // Just transitioned to not-in-progress (or mounted already finished):
    // keep listening for the grace window, then stop.
    const timeoutId = window.setTimeout(
      () => setActive(false),
      LIVE_LISTENER_GRACE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isInProgress]);

  return active;
}
